import { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { FaSave } from 'react-icons/fa';
import CustomButton from 'base/ui/components/Button';
import { TransitionButtonModel } from 'models/PurchaseOrder';
import { IWasteHandoverPlanServices } from 'services/Main/wasteHandoverPlan_services';
import TransitionButtonModal from './TransitionButtonModal';
import ConfirmHandoverModal from './ConfirmHandoverModal';
import OutboundWeighingModal from './OutboundWeighingModal';
import { DeliveryInfoItem } from 'models/WasteHandoverRegistration';

interface ActionButtonsProps {
    wasteHandoverPlanServices: IWasteHandoverPlanServices;
    wasteHandoverRegistrationId?: string;
    wasteHandoverPlanId?: string;
    wasteHandoverRegistrationCode?: string;
    deliveryInfoItems?: DeliveryInfoItem[];
    loading: boolean;
    disabledField: () => boolean;
    hideSaveButtons?: boolean;
    onSave: () => void;
    onUpdate: (option?: {
        isHideMessage?: boolean;
        isContinue?: boolean;
        tareData?: {
            noPacking: number;
            perPackingWeight: number;
            packingWeight: number;
        };
        confirmedDetailIds?: string[]; // Danh sách các item được chọn "XN bàn giao" (DBG)
        deliveryStatusesMap?: Record<string, string>; // Map deliveryStatuses để cập nhật deliveryStatus
        outboundWeighingData?: {
            secondWeight: number;
            deliveredQuantity: number;
        }; // Dữ liệu cân ra
    }) => Promise<boolean>;
    onReload?: () => Promise<void>;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
    wasteHandoverPlanServices,
    wasteHandoverRegistrationId,
    wasteHandoverPlanId,
    wasteHandoverRegistrationCode,
    deliveryInfoItems = [],
    loading,
    disabledField,
    hideSaveButtons = false,
    onSave,
    onUpdate,
    onReload,
}) => {
    const { t } = useTranslation();
    const [transitionBtns, setTransitionBtns] = useState<TransitionButtonModel[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedButton, setSelectedButton] = useState<TransitionButtonModel | undefined>(undefined);
    const [confirmHandoverModalOpen, setConfirmHandoverModalOpen] = useState(false);
    const [selectedConfirmButton, setSelectedConfirmButton] = useState<TransitionButtonModel | undefined>(undefined);
    const [outboundWeighingModalOpen, setOutboundWeighingModalOpen] = useState(false);
    const [selectedOutboundWeighingButton, setSelectedOutboundWeighingButton] = useState<TransitionButtonModel | undefined>(undefined);

    // Lấy danh sách transition buttons
    const getTransitionButton = async () => {
        try {
            if (wasteHandoverRegistrationId) {
                const result = await wasteHandoverPlanServices.getTransitionButton(wasteHandoverRegistrationId);
                if (result.isSuccess) {
                    setTransitionBtns(result.data || []);
                }
            }
        } catch (error) {
            console.error('Error loading transition buttons:', error);
        }
    };

    // Thực thi transition button
    const executeTransitionButton = async (data: {
        taskId: string;
        statusTransitionId: string;
        comment?: string;
        files?: string[];
        documentNumber?: string;
        deliveryStatuses?: Array<{
            wasteHandoverRegistrationDetailId: string;
            deliveryStatus: string;
        }>;
        numberOfTare?: number;
        tareWeight?: number;
        tareDeduction?: number;
    }): Promise<boolean> => {
        try {
            if (wasteHandoverRegistrationId) {
                const result = await wasteHandoverPlanServices.executeTransitionButton({
                    taskId: data.taskId,
                    statusTransitionId: data.statusTransitionId,
                    comment: data.comment,
                    files: data.files,
                    documentNumber: data.documentNumber,
                });
                if (result.isSuccess) {
                    message.success(result.message || t('message.success'));
                    // Reload lại danh sách buttons
                    await getTransitionButton();
                    return true;
                } else {
                    message.error(result.message || t('message.error'));
                    return false;
                }
            }
            return false;
        } catch (error) {
            console.error('Error executing transition button:', error);
            message.error(t('message.error'));
            return false;
        }
    };

    useEffect(() => {
        if (wasteHandoverRegistrationId) {
            getTransitionButton();
        }
    }, [wasteHandoverRegistrationId]);

    return (
        <>
            {/* Transition buttons */}
            {wasteHandoverRegistrationId && transitionBtns.map((btn) => (
                <CustomButton
                    key={btn.statusTransitionId}
                    type="primary"
                    color={btn.color}
                    style={{ color: btn.textColor }}
                    loading={loading}
                    onClick={() => {
                        // Xác định có phải là "Vào cổng", "Từ chối vào cổng", "Xác nhận kế hoạch bàn giao", "Xác nhận đã cân", "Ra cổng", "Xác nhận bàn giao", hoặc "Cân ra" không
                        const isCheckIn = btn.toStatusCode === 'VC';
                        const isRejectEntry = btn.toStatusCode === 'TCVC';
                        const isConfirmPlan = btn.toStatusCode === 'XACNHAN';
                        const isConfirmWeighed = btn.toStatusCode === 'CANVAO';
                        const isConfirmHandover = btn.toStatusCode === 'CCR'; // Chờ cân ra
                        const isOutboundWeighing = btn.toStatusCode === 'CANRA'; // Cân ra
                        const isExitGate = btn.toStatusCode === 'RC';

                        // Mở modal "Xác nhận bàn giao" nếu là "Chờ cân ra"
                        if (isConfirmHandover) {
                            setSelectedConfirmButton(btn);
                            setConfirmHandoverModalOpen(true);
                        }
                        // Mở modal "Cân ra" nếu là "CANRA"
                        else if (isOutboundWeighing) {
                            setSelectedOutboundWeighingButton(btn);
                            setOutboundWeighingModalOpen(true);
                        }
                        // Mở modal nếu là "Vào cổng", "Từ chối vào cổng", "Xác nhận kế hoạch bàn giao", "Xác nhận đã cân", "Ra cổng", hoặc cần comment
                        else if (isCheckIn || isRejectEntry || isConfirmPlan || isConfirmWeighed || isExitGate || btn.isRequiredComment) {
                            setSelectedButton(btn);
                            setModalOpen(true);
                        } else if (btn.isBackBtn) {
                            // Back button thực thi luôn
                            onUpdate({ isHideMessage: true, isContinue: true }).then(async (updateSuccess) => {
                                if (updateSuccess) {
                                    const executeSuccess = await executeTransitionButton({
                                        taskId: wasteHandoverRegistrationId,
                                        statusTransitionId: btn.statusTransitionId,
                                    });
                                    if (executeSuccess && onReload) {
                                        await onReload();
                                    }
                                }
                            });
                        } else {
                            // Các button khác không cần comment, thực thi luôn
                            onUpdate({ isHideMessage: true, isContinue: true }).then(async (updateSuccess) => {
                                if (updateSuccess) {
                                    const executeSuccess = await executeTransitionButton({
                                        taskId: wasteHandoverRegistrationId,
                                        statusTransitionId: btn.statusTransitionId,
                                    });
                                    if (executeSuccess && onReload) {
                                        await onReload();
                                    }
                                }
                            });
                        }
                    }}
                >
                    {btn.statusTransitionName}
                </CustomButton>
            ))}

            {/* Transition Button Modal */}
            {selectedButton && (
                <TransitionButtonModal
                    open={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedButton(undefined);
                    }}
                    buttonInfo={selectedButton}
                    taskId={wasteHandoverRegistrationId || ''}
                    wasteHandoverRegistrationCode={wasteHandoverRegistrationCode}
                    onUpdate={async () => {
                        return await onUpdate({ isHideMessage: true, isContinue: true });
                    }}
                    onExecute={executeTransitionButton}
                    onReload={async () => {
                        // Reload detail registration
                        if (onReload) {
                            await onReload();
                        }
                        // Reload lại danh sách transition buttons
                        await getTransitionButton();
                    }}
                />
            )}

            {/* Confirm Handover Modal */}
            {selectedConfirmButton && (
                <ConfirmHandoverModal
                    open={confirmHandoverModalOpen}
                    onClose={() => {
                        setConfirmHandoverModalOpen(false);
                        setSelectedConfirmButton(undefined);
                    }}
                    buttonInfo={selectedConfirmButton}
                    taskId={wasteHandoverRegistrationId || ''}
                    wasteHandoverRegistrationCode={wasteHandoverRegistrationCode}
                    deliveryInfoItems={deliveryInfoItems}
                    onUpdate={async (tareData?: { numberOfTare: number; tareWeight: number; tareDeduction: number }, confirmedDetailIds?: string[], deliveryStatusesMap?: Record<string, string>) => {
                        // Nếu có tareData, cần truyền vào onUpdate để update các field trừ bì
                        return await onUpdate({
                            isHideMessage: true,
                            isContinue: true,
                            tareData: tareData ? {
                                noPacking: tareData.numberOfTare,
                                perPackingWeight: tareData.tareWeight,
                                packingWeight: tareData.tareDeduction,
                            } : undefined,
                            confirmedDetailIds: confirmedDetailIds, // Danh sách các item được chọn "XN bàn giao"
                            deliveryStatusesMap: deliveryStatusesMap, // Map deliveryStatuses để cập nhật deliveryStatus
                        });
                    }}
                    onExecute={executeTransitionButton}
                    onReload={async () => {
                        // Reload detail registration
                        if (onReload) {
                            await onReload();
                        }
                        // Reload lại danh sách transition buttons
                        await getTransitionButton();
                    }}
                />
            )}

            {/* Outbound Weighing Modal */}
            {selectedOutboundWeighingButton && (
                <OutboundWeighingModal
                    open={outboundWeighingModalOpen}
                    onClose={() => {
                        setOutboundWeighingModalOpen(false);
                        setSelectedOutboundWeighingButton(undefined);
                    }}
                    buttonInfo={selectedOutboundWeighingButton}
                    taskId={wasteHandoverRegistrationId || ''}
                    wasteHandoverRegistrationCode={wasteHandoverRegistrationCode}
                    onUpdate={async (outboundWeighingData?: { secondWeight: number; deliveredQuantity: number }) => {
                        return await onUpdate({
                            isHideMessage: true,
                            isContinue: true,
                            outboundWeighingData: outboundWeighingData,
                        });
                    }}
                    onExecute={executeTransitionButton}
                    onReload={async () => {
                        // Reload detail registration
                        if (onReload) {
                            await onReload();
                        }
                        // Reload lại danh sách transition buttons
                        await getTransitionButton();
                    }}
                />
            )}

            {/* Nút Lưu */}
            {!hideSaveButtons && !disabledField() && (
                <>
                    <CustomButton
                        loading={loading}
                        onClick={() => {
                            if (wasteHandoverPlanId || wasteHandoverRegistrationId) {
                                onUpdate();
                            } else {
                                onSave();
                            }
                        }}
                        type="primary"
                    >
                        <FaSave />
                        {t('btn.save')}
                    </CustomButton>
                    {wasteHandoverPlanId && (
                        <CustomButton
                            loading={loading}
                            onClick={() => {
                                onUpdate({ isContinue: true });
                            }}
                            type="primary"
                        >
                            <FaSave />
                            {t('waste-handover-plan-register.save-and-continue')}
                        </CustomButton>
                    )}
                </>
            )}
        </>
    );
};

export default ActionButtons;

